import type { WorkflowFact } from '../facts'

/**
 * Facts are parsed upstream (from Nebula's `facts_schema` when it has one, else
 * the db text column) so this only renders. `rawText` is that db column verbatim,
 * shown as a last resort when it produced no fact rows.
 */
export function WorkflowFacts({
  facts,
  rawText = '',
}: {
  facts: WorkflowFact[]
  rawText?: string
}) {
  const trimmed = rawText.trim()

  if (facts.length === 0) {
    if (!trimmed) {
      return (
        <p className="text-sm text-muted-foreground italic">
          No facts defined for this workflow.
        </p>
      )
    }
    // Couldn't parse into id/description pairs — show the raw text.
    return (
      <p className="text-sm whitespace-pre-wrap text-primary-high-contrast">
        {trimmed}
      </p>
    )
  }

  const required = facts.filter((f) => f.required)
  const optional = facts.filter((f) => !f.required)

  return (
    <div className="flex flex-col gap-4">
      <FactGroup label="Required" tone="required" facts={required} />
      <FactGroup label="Optional" tone="optional" facts={optional} />
    </div>
  )
}

function FactGroup({
  label,
  tone,
  facts,
}: {
  label: string
  tone: 'required' | 'optional'
  facts: WorkflowFact[]
}) {
  if (facts.length === 0) return null

  const dot = tone === 'required' ? 'bg-indigo-500' : 'bg-muted-foreground/40'

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className={`size-1.5 rounded-full ${dot}`} />
        {label}
        <span className="text-muted-foreground/60">({facts.length})</span>
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-lg border bg-card">
        {facts.map((fact) => (
          <FactRow key={fact.id} fact={fact} />
        ))}
      </div>
    </div>
  )
}

function FactRow({ fact }: { fact: WorkflowFact }) {
  return (
    <div className="flex flex-col gap-1 px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-4">
      {/* Title leads; the id stays alongside it because this page documents the
          contract a caller writes against. */}
      <div className="flex w-fit shrink-0 flex-col gap-0.5 sm:w-48">
        <span className="text-sm font-medium text-primary-high-contrast">
          {fact.title}
        </span>
        <code className="w-fit font-mono text-[11px] text-muted-foreground">
          {fact.id}
        </code>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm text-muted-foreground">{fact.description}</p>
        {/* Shown because a caller reading the contract needs to know which facts
            they can leave out — `default` is already display-ready (structured,
            blank and secret defaults are dropped upstream). */}
        {fact.default !== undefined && (
          <p className="text-xs text-muted-foreground/80">
            Defaults to{' '}
            <code className="font-mono">{String(fact.default)}</code>
          </p>
        )}
        {/* Says the value is withheld rather than absent. `factsFromSchema` has
            already dropped a secret fact's default, so there is nothing here to
            leak — this only keeps the empty row from reading as a fact that
            declares no default. */}
        {fact.secret && (
          <p className="text-xs text-muted-foreground/80">
            Value is a secret, resolved by the workflow runner
          </p>
        )}
      </div>
    </div>
  )
}
